import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import Logo from "@/components/Logo";
import LocationBadge from "@/components/LocationBadge";
import { Menu, X, LayoutDashboard, LogOut } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/courses", label: "Courses" },
  { to: "/webinars", label: "Webinars" },
  { to: "/industries", label: "Industries" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/about", label: "About" },
];

export default function Header() {
  const { user, logout } = useApp();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 glass" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between gap-4">
        <div className="flex items-center gap-8">
          <Logo />
          <nav className="hidden lg:flex items-center gap-6">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-sm text-slate-600 hover:text-slate-900 transition-colors"
              >
                {n.label}
              </Link>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden sm:block"><LocationBadge /></div>
          <Link to="/webinars" data-testid="header-join-webinar" className="hidden md:inline-flex btn-primary px-5 py-2 text-sm">
            Join a Webinar
          </Link>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="h-9 w-9 rounded-full bg-asf-orange/20 border border-asf-orange/40 grid place-items-center text-sm font-semibold">
                  {(user.name || "U").charAt(0).toUpperCase()}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-slate-100 border-slate-200 text-slate-900">
                <div className="px-2 py-1.5 text-xs text-slate-500 max-w-[180px] truncate">{user.email}</div>
                <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate(user.role === "admin" ? "/admin" : "/dashboard")} className="cursor-pointer gap-2 focus:bg-slate-200 focus:text-slate-900">
                  <LayoutDashboard className="h-4 w-4" /> {user.role === "admin" ? "Admin" : "My Dashboard"}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); navigate("/"); }} className="cursor-pointer gap-2 focus:bg-slate-200 focus:text-slate-900">
                  <LogOut className="h-4 w-4" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="hidden md:flex items-center gap-2">
              <Link to="/login" data-testid="header-login" className="text-sm text-slate-600 hover:text-slate-900 transition-colors px-3">Login</Link>
              <Link to="/register" data-testid="header-register" className="btn-secondary px-5 py-2 text-sm">Register</Link>
            </div>
          )}
          <button className="lg:hidden text-slate-700" onClick={() => setMobileOpen((v) => !v)} data-testid="mobile-menu-toggle">
            {mobileOpen ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden glass border-t border-slate-200 px-4 py-4 space-y-2" data-testid="mobile-menu">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setMobileOpen(false)} className="block py-2 text-slate-700">{n.label}</Link>
          ))}
          <div className="pt-2"><LocationBadge /></div>
          {!user && (
            <div className="flex gap-2 pt-2">
              <Link to="/login" onClick={() => setMobileOpen(false)} className="btn-secondary px-4 py-2 text-sm flex-1 text-center">Login</Link>
              <Link to="/register" onClick={() => setMobileOpen(false)} className="btn-primary px-4 py-2 text-sm flex-1 text-center">Register</Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
